// @ts-check
// Editor da campanha de WhatsApp em rascunho ou agendada (spec/whatsapp.md 18.2; contrato 09 de campanhas de WhatsApp,
// seção 14): quatro cartões (tipo e número, público, mensagem, envio) e o resumo ao lado, com a pré-checagem do servidor
// (papel de quem edita). Salva sozinho 2 s depois da última mudança; "Revisar e enviar" fica desligado enquanto falta
// algo. Agendada: só leitura, com "Desagendar".
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { hasCurrentPermissionFlag, objectsByNameSingular } from '../../data/metadata-store.js';
import { listChannels } from '../../data/conversations.js';
import { listWhatsappTemplates, syncWhatsappTemplates } from '../../data/integrations.js';
import { findManyRecords } from '../../data/rest.js';
import {
  deleteWhatsappCampaign, duplicateWhatsappCampaign, precheckWhatsappCampaign, unscheduleWhatsappCampaign, updateWhatsappCampaign,
} from '../../data/whatsapp-campaigns.js';
import { getErrorMessage } from '../../data/api-client.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { AudienceBuilder } from '../audiences/audience-builder.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, MenuItem } from '../../ui/dropdown.js';
import { Skeleton, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatCurrency, formatDateTime, formatNumber } from '../../lib/format.js';
import { getRecordLabel } from '../../lib/field-metadata.js';
import { campaignTemplateNewHref, channelLabel } from '../../lib/whatsapp-templates.js';
import { missingCampaignParts, sampleValues, sendWindowSummary, templateUnavailableReason, topExclusions } from '../../lib/whatsapp-campaigns.js';
import { validateVariableMappings } from '../../../../shared/whatsapp/template-variables.js';
import { createEmptyAudienceDefinition } from '../../../../shared/audiences/audience-definition.js';
import { WHATSAPP_CAMPAIGN_LIMITS } from '../../../../shared/whatsapp/whatsapp-campaign-settings.js';
import { WhatsappTemplateGroupedPicker } from './whatsapp-template-grouped-picker.js';
import { VariableMappingEditor } from './variable-mapping-editor.js';
import { WhatsappMessagePreview } from './whatsapp-message-preview.js';
import { WhatsappCampaignReviewModal } from './whatsapp-campaign-review-modal.js';
import { WhatsappCampaignTestDialog } from './whatsapp-campaign-test-dialog.js';
import { CampaignKindBadge, CampaignStatusBadge, whatsappCampaignHref, deliveryReasonText } from './whatsapp-campaigns-page.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/whatsapp-campaigns.js').WhatsappCampaign} WhatsappCampaign
 * @typedef {import('../../data/whatsapp-campaigns.js').WhatsappCampaignPrecheck} WhatsappCampaignPrecheck
 * @typedef {import('../../data/integrations.js').WhatsappTemplate} WhatsappTemplate
 * @typedef {import('../../data/conversations.js').Channel} Channel
 */

const AUTOSAVE_DELAY_MILLISECONDS = 2000;
const PRECHECK_DELAY_MILLISECONDS = 600;

/**
 * Cartão numerado do editor.
 * @param {{ number: number, title: string, missing?: string[], children: any, testId?: string }} props
 */
function EditorCard({ number, title, missing = [], children, testId }) {
  return html`<section class="wa-editor-card" data-testid=${testId} aria-labelledby=${`wa-editor-card-${number}`}>
    <header class="wa-editor-card-header">
      <span class="wa-editor-card-number" aria-hidden="true">${number}</span>
      <h2 id=${`wa-editor-card-${number}`}>${title}</h2>
    </header>
    ${missing.length > 0
      ? html`<ul class="wa-editor-missing">${missing.map((key) => html`<li key=${key}><${Icon} name="IconAlertCircle" size=${14} />${t(`whatsappCampaigns.editor.missing.${key}`)}</li>`)}</ul>`
      : null}
    <div class="wa-editor-card-body">${children}</div>
  </section>`;
}

/** Aviso dentro de um cartão. @param {{ tone?: 'info' | 'warning' | 'danger', icon?: string, children: any }} props */
function Notice({ tone = 'info', icon = 'IconInfoCircle', children }) {
  return html`<div class="wa-notice" data-tone=${tone} role=${tone === 'danger' ? 'alert' : undefined}><${Icon} name=${icon} size=${16} /><div class="wa-notice-body">${children}</div></div>`;
}

/**
 * Resumo ao lado: público, elegíveis, fora por motivo, hoje cabem, dias e custo.
 * @param {{ precheck: WhatsappCampaignPrecheck | null, state: 'idle' | 'loading' | 'error' | 'incomplete', error: string | null, missing: string[] }} props
 */
function EditorSummary({ precheck, state, error, missing }) {
  const [isOpen, setIsOpen] = useState(true);
  const exclusions = topExclusions(precheck?.audience.excluded);
  return html`<aside class="wa-editor-summary" aria-label=${t('whatsappCampaigns.summary.title')}>
    <button type="button" class="wa-editor-summary-toggle" aria-expanded=${isOpen ? 'true' : 'false'} onClick=${() => setIsOpen(!isOpen)}>
      <span>${t('whatsappCampaigns.summary.title')}</span><${Icon} name=${isOpen ? 'IconChevronUp' : 'IconChevronDown'} size=${14} />
    </button>
    ${!isOpen ? null : state === 'incomplete'
      ? html`<p class="wa-editor-summary-empty">${t('whatsappCampaigns.summary.incomplete')}</p>
          <ul class="wa-editor-missing">${missing.map((key) => html`<li key=${key}>${t(`whatsappCampaigns.editor.missing.${key}`)}</li>`)}</ul>`
      : state === 'error'
        ? html`<p class="wa-editor-summary-empty" role="alert">${error ?? t('whatsappCampaigns.summary.failed')}</p>`
        : !precheck
          ? html`<div aria-busy="true"><${Skeleton} height=${16} /><${Skeleton} height=${16} width="70%" /></div>`
          : html`<dl class="wa-editor-summary-facts" aria-busy=${state === 'loading' ? 'true' : 'false'}>
              <div><dt>${t('whatsappCampaigns.summary.cards')}</dt><dd>${formatNumber(precheck.audience.cards)}${precheck.audience.isEstimate ? ` ${t('whatsappCampaigns.summary.estimate')}` : ''}</dd></div>
              <div><dt>${t('whatsappCampaigns.summary.toSend')}</dt><dd data-testid="wa-summary-to-send">${formatNumber(precheck.toSend)}</dd></div>
              ${exclusions.map((item) => html`<div key=${item.reason} class="wa-editor-summary-exclusion"><dt>${deliveryReasonText(item.reason)}</dt><dd>${formatNumber(item.count)}</dd></div>`)}
              ${precheck.audience.serviceWithoutRelationship > 0
                ? html`<p class="wa-editor-summary-note">${t('whatsappCampaigns.summary.serviceWithoutRelationship', { count: precheck.audience.serviceWithoutRelationship })}</p>`
                : null}
              ${precheck.portfolio
                ? html`<div><dt>${t('whatsappCampaigns.limits.todayFitsLabel')}</dt><dd>${precheck.portfolio.availableNow === null
                    ? t('whatsappCampaigns.limits.noLimit')
                    : formatNumber(precheck.portfolio.availableNow)}</dd></div>
                  ${precheck.portfolio.estimatedDays && precheck.portfolio.estimatedDays > 1
                    ? html`<p class="wa-editor-summary-note">${t('whatsappCampaigns.limits.days', { days: precheck.portfolio.estimatedDays })}</p>`
                    : null}`
                : null}
              <div><dt>${t('whatsappCampaigns.summary.cost')}</dt><dd>${precheck.cost?.isAvailable
                ? formatCurrency(Number(precheck.cost.estimatedTotal ?? 0), precheck.cost.currency ?? 'BRL')
                : t('whatsappCampaigns.summary.costUnavailable')}</dd></div>
            </dl>`}
  </aside>`;
}

/** @param {{ campaignId: string, initialCampaign: WhatsappCampaign, onChanged: (campaign: WhatsappCampaign) => void }} props */
export function WhatsappCampaignEditorPage({ campaignId, initialCampaign, onChanged }) {
  const isMobile = useIsMobile();
  const [campaign, setCampaign] = useState(initialCampaign);
  const [draft, setDraft] = useState(/** @type {Record<string, any>} */ ({}));
  const [saveState, setSaveState] = useState(/** @type {'saved' | 'dirty' | 'saving' | 'error'} */ ('saved'));
  const [saveError, setSaveError] = useState(/** @type {string | null} */ (null));
  const [channels, setChannels] = useState(/** @type {Channel[] | null} */ (null));
  const [templates, setTemplates] = useState(/** @type {WhatsappTemplate[] | null} */ (null));
  const [isSyncing, setIsSyncing] = useState(false);
  const [mediaCampaigns, setMediaCampaigns] = useState(/** @type {Array<{ id: string, name: string }> | null} */ (null));
  const [precheck, setPrecheck] = useState(/** @type {WhatsappCampaignPrecheck | null} */ (null));
  const [precheckState, setPrecheckState] = useState(/** @type {'idle' | 'loading' | 'error' | 'incomplete'} */ ('idle'));
  const [precheckError, setPrecheckError] = useState(/** @type {string | null} */ (null));
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isTestOpen, setIsTestOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuAnchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const saveTimer = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const pendingPatch = useRef(/** @type {Record<string, any>} */ ({}));
  const isAdmin = hasCurrentPermissionFlag('WORKSPACE');
  const isScheduled = campaign.status === 'SCHEDULED';
  const readOnly = isScheduled;

  // o que aparece na tela: o salvo mais o que ainda não foi salvo
  const view = /** @type {Record<string, any>} */ ({ ...campaign, ...draft });
  const kind = /** @type {'MARKETING' | 'ANNOUNCEMENT'} */ (view.kind);
  const channelId = draft.channelId !== undefined ? draft.channelId : campaign.channel?.id ?? null;
  const templateId = draft.templateId !== undefined ? draft.templateId : campaign.template?.id ?? null;
  const selectedTemplate = templates?.find((template) => template.id === templateId) ?? null;
  const variableMappings = view.variableMappings ?? { body: {}, header: {} };
  const missingVariables = selectedTemplate ? validateVariableMappings(selectedTemplate, variableMappings).missing : [];
  const missing = missingCampaignParts({
    name: view.name, channel: channelId ? { id: channelId } : null, template: templateId ? { id: templateId } : null,
    variableMappings, audienceDefinition: view.audienceDefinition,
  }, selectedTemplate);
  const channel = channels?.find((item) => item.id === channelId) ?? null;

  useEffect(() => {
    // rascunho novo: o construtor já mostra "Todos os leads"; grava esse público para a tela não pedir o que já mostra
    if (!readOnly && !initialCampaign.audienceDefinition) change({ audienceDefinition: createEmptyAudienceDefinition() });
    listChannels().then((data) => setChannels(data.channels.filter((item) => item.type === 'WHATSAPP'))).catch(() => setChannels([]));
    listWhatsappTemplates({}).then(setTemplates).catch(() => setTemplates([]));
    const mediaObject = objectsByNameSingular.value.get('campaign');
    if (mediaObject) {
      findManyRecords(mediaObject, { limit: 100, depth: 0 })
        .then((result) => setMediaCampaigns(result.records.map((record) => ({ id: String(record.id), name: getRecordLabel(mediaObject, record) }))))
        .catch(() => setMediaCampaigns(null));
    }
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const runPrecheck = () => {
    if (missing.length > 0) {
      setPrecheckState('incomplete');
      return;
    }
    setPrecheckState('loading');
    precheckWhatsappCampaign(campaignId)
      .then((result) => {
        setPrecheck(result);
        setPrecheckState('idle');
      })
      .catch((error) => {
        setPrecheckError(getErrorMessage(error));
        setPrecheckState('error');
      });
  };
  // a pré-checagem acompanha o rascunho salvo (o servidor lê a campanha gravada)
  useEffect(() => {
    if (saveState !== 'saved') return undefined;
    const timer = setTimeout(runPrecheck, PRECHECK_DELAY_MILLISECONDS);
    return () => clearTimeout(timer);
  }, [campaign.updatedAt, saveState, missing.join(',')]);

  const save = async () => {
    const patch = pendingPatch.current;
    pendingPatch.current = {};
    if (Object.keys(patch).length === 0) return campaign;
    setSaveState('saving');
    try {
      const saved = await updateWhatsappCampaign(campaignId, patch);
      setCampaign(saved);
      onChanged(saved);
      setDraft((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !(key in patch) || current[key] !== patch[key])));
      setSaveState(Object.keys(pendingPatch.current).length > 0 ? 'dirty' : 'saved');
      setSaveError(null);
      return saved;
    } catch (error) {
      // o que foi digitado fica no rascunho local até salvar de novo
      pendingPatch.current = { ...patch, ...pendingPatch.current };
      setSaveState('error');
      setSaveError(getErrorMessage(error));
      return null;
    }
  };
  /** @param {Record<string, any>} change */
  const change = (change) => {
    if (readOnly) return;
    setDraft((current) => ({ ...current, ...change }));
    pendingPatch.current = { ...pendingPatch.current, ...change };
    setSaveState('dirty');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      save();
    }, AUTOSAVE_DELAY_MILLISECONDS);
  };
  const saveNow = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    return save();
  };

  const refreshTemplates = async () => {
    setIsSyncing(true);
    try {
      if (channelId) await syncWhatsappTemplates(channelId);
      setTemplates(await listWhatsappTemplates({}));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSyncing(false);
    }
  };
  const openReview = async () => {
    const saved = await saveNow();
    if (saved) setIsReviewOpen(true);
  };
  const duplicate = async () => {
    setIsMenuOpen(false);
    try {
      const copy = await duplicateWhatsappCampaign(campaignId);
      navigate(whatsappCampaignHref(copy.id));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  const remove = async () => {
    try {
      await deleteWhatsappCampaign(campaignId);
      showSuccessSnackbar(t('whatsappCampaigns.actions.deleted'));
      navigate('/campaigns/whatsapp');
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  const unschedule = async () => {
    try {
      const updated = await unscheduleWhatsappCampaign(campaignId);
      setCampaign(updated);
      onChanged(updated);
      showSuccessSnackbar(t('whatsappCampaigns.editor.unscheduled'));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  const templateCreateHref = campaignTemplateNewHref({ from: whatsappCampaignHref(campaignId), channelId, kind });
  const templatesOfChannel = templates === null ? null : templates.filter((template) => !channelId || template.channelId === channelId);
  const sample = sampleValues(selectedTemplate ?? {}, precheck?.sample);
  const businessHours = sendWindowSummary();
  const reviewDisabledReason = missing.length > 0 ? missing.map((key) => t(`whatsappCampaigns.editor.missing.${key}`)).join(' ') : null;

  const headerActions = html`<div class="wa-editor-actions">
    <span class="wa-editor-save-state" data-state=${saveState} aria-live="polite">${saveState === 'saved'
      ? t('whatsappCampaigns.editor.saved')
      : saveState === 'saving' ? t('whatsappCampaigns.editor.saving') : saveState === 'error' ? t('whatsappCampaigns.editor.saveFailed') : t('whatsappCampaigns.editor.unsaved')}</span>
    ${readOnly ? null : html`<${Button} title=${t('whatsappCampaigns.editor.save')} variant="secondary" size="small" disabled=${saveState === 'saved' || saveState === 'saving'} onClick=${saveNow} />`}
    <${Button} title=${t('whatsappCampaigns.test.open')} icon="IconSend" variant="secondary" size="small" disabled=${!templateId || !channelId} onClick=${() => setIsTestOpen(true)} />
    ${readOnly ? null : html`<span title=${reviewDisabledReason ?? undefined}>
      <${Button} title=${t('whatsappCampaigns.editor.review')} icon="IconChecklist" accent="blue" size="small" disabled=${missing.length > 0 || saveState === 'saving'}
        onClick=${openReview} data-testid="wa-editor-review" />
    </span>`}
    <span ref=${menuAnchorRef}>
      <${LightIconButton} icon="IconDotsVertical" ariaLabel=${t('whatsappCampaigns.actions.more')} onClick=${() => setIsMenuOpen(!isMenuOpen)} />
      <${Dropdown} anchorRef=${menuAnchorRef} isOpen=${isMenuOpen} onClose=${() => setIsMenuOpen(false)} width=${200} placement="bottom-end" role="menu">
        <${DropdownMenuItems}>
          <${MenuItem} icon="IconCopy" text=${t('whatsappCampaigns.actions.duplicate')} onClick=${duplicate} />
          <${MenuItem} icon="IconTrash" accent="danger" text=${t('whatsappCampaigns.actions.delete')} onClick=${() => {
            setIsMenuOpen(false);
            setIsDeleteOpen(true);
          }} />
        <//>
      <//>
    </span>
  </div>`;

  const breadcrumb = html`<a class="wa-editor-back" href="/campaigns/whatsapp" onClick=${(/** @type {MouseEvent} */ event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    navigate('/campaigns/whatsapp');
  }}><${Icon} name="IconChevronLeft" size=${14} /><span class="wa-editor-back-label">${t('whatsappCampaigns.title')}</span></a>`;
  const badges = html`<${CampaignKindBadge} kind=${kind} /><${CampaignStatusBadge} campaign=${campaign} />`;

  return html`<${PageCard} className="campaigns-screen wa-campaign-editor-page"
    header=${html`<${PageHeader} icon="IconBrandWhatsapp" breadcrumb=${breadcrumb} actions=${isMobile ? null : headerActions} title=${html`<span class="wa-editor-title">
      <input class="wa-editor-name" value=${view.name} maxLength=${WHATSAPP_CAMPAIGN_LIMITS.maximumNameLength} aria-label=${t('whatsappCampaigns.editor.name')}
        disabled=${readOnly} onInput=${(/** @type {Event} */ event) => change({ name: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
      ${isMobile ? null : badges}
    </span>`} />`}
  >
    ${isMobile ? html`<div class="wa-mobile-toolbar">${badges}${headerActions}</div>` : null}
    ${saveError ? html`<${Notice} tone="danger" icon="IconAlertTriangle"><p>${saveError}</p></${Notice}>` : null}
    ${isScheduled
      ? html`<div class="wa-editor-scheduled" role="status">
          <${Icon} name="IconCalendarTime" size=${16} />
          <span>${t('whatsappCampaigns.editor.scheduledFor', { date: formatDateTime(campaign.scheduledAt) })}</span>
          <${Button} title=${t('whatsappCampaigns.editor.unschedule')} variant="secondary" size="small" onClick=${unschedule} />
        </div>`
      : null}
    <div class="wa-editor-layout">
      <div class="wa-editor-main">
        <${EditorCard} number=${1} title=${t('whatsappCampaigns.editor.steps.kindAndNumber')} missing=${missing.filter((key) => key === 'channel' || key === 'name')} testId="wa-editor-kind">
          <fieldset class="wa-editor-kinds" disabled=${readOnly}>
            <legend class="wa-visually-hidden">${t('whatsappCampaigns.editor.kindLabel')}</legend>
            ${['MARKETING', 'ANNOUNCEMENT'].map((option) => html`<label key=${option} class="wa-editor-kind" data-selected=${kind === option ? 'true' : undefined}>
              <input type="radio" name="wa-campaign-kind" value=${option} checked=${kind === option}
                onChange=${() => change({ kind: option, ...(selectedTemplate && templateUnavailableReason(selectedTemplate, /** @type {any} */ (option)) ? { templateId: null } : {}) })} />
              <span><strong>${t(`whatsappCampaigns.kind.${option}`)}</strong><small>${t(option === 'MARKETING' ? 'whatsappCampaigns.newMarketingHint' : 'whatsappCampaigns.newAnnouncementHint')}</small></span>
            </label>`)}
          </fieldset>
          ${kind === 'ANNOUNCEMENT' ? html`<${Notice} tone="warning" icon="IconAlertTriangle"><p>${t('whatsappCampaigns.kind.announcementWarning')}</p><//>` : null}
          <label class="form-field">
            <span class="form-field-label">${t('whatsappCampaigns.editor.number')}</span>
            <select class="text-input integration-select" value=${channelId ?? ''} disabled=${readOnly || channels === null}
              onChange=${(/** @type {Event} */ event) => {
                const value = /** @type {HTMLSelectElement} */ (event.currentTarget).value || null;
                change({ channelId: value, ...(selectedTemplate && selectedTemplate.channelId !== value ? { templateId: null } : {}) });
              }}>
              <option value="" disabled selected=${!channelId}>${t('whatsappCampaigns.editor.chooseNumber')}</option>
              ${(channels ?? []).map((item) => html`<option key=${item.id} value=${item.id} selected=${item.id === channelId} disabled=${item.isActive === false}>${channelLabel(item)}</option>`)}
            </select>
          </label>
          ${channel && channel.isActive === false
            ? html`<${Notice} tone="danger" icon="IconPlugConnectedX"><p>${t('whatsappCampaigns.editor.channelDisconnected')}</p>
                ${isAdmin ? html`<a href="/settings/channels" class="integration-link">${t('whatsappCampaigns.noChannel.goToChannels')}</a>` : null}<//>`
            : null}
        <//>

        <${EditorCard} number=${2} title=${t('whatsappCampaigns.editor.steps.audience')} missing=${missing.filter((key) => key === 'audience')} testId="wa-editor-audience">
          ${kind === 'ANNOUNCEMENT' ? html`<p class="wa-editor-help">${t('whatsappCampaigns.editor.announcementAudienceHelp')}</p>` : null}
          <${AudienceBuilder} value=${view.audienceDefinition} channels=${['WHATSAPP']} purpose=${kind === 'MARKETING' ? 'MARKETING' : 'SERVICE'} readOnly=${readOnly}
            title=${t('whatsappCampaigns.editor.audienceTitle')} onChange=${(definition) => change({ audienceDefinition: definition })} />
        <//>

        <${EditorCard} number=${3} title=${t('whatsappCampaigns.editor.steps.message')} missing=${missing.filter((key) => key === 'template' || key === 'variables')} testId="wa-editor-message">
          ${selectedTemplate && templateUnavailableReason(selectedTemplate, kind)
            ? html`<${Notice} tone="danger" icon="IconAlertTriangle"><p>${t(`whatsappCampaigns.editor.templateUnavailable.${templateUnavailableReason(selectedTemplate, kind)}`)}</p><//>`
            : null}
          ${!channelId
            ? html`<p class="wa-editor-help">${t('whatsappCampaigns.editor.chooseNumberFirst')}</p>`
            : html`<${WhatsappTemplateGroupedPicker} templates=${templatesOfChannel} context=${kind === 'MARKETING' ? 'MARKETING_CAMPAIGN' : 'ANNOUNCEMENT'}
                selectedId=${templateId} label=${t('whatsappCampaigns.editor.template')}
                disabledReason=${(/** @type {WhatsappTemplate} */ template) => {
                  if (readOnly) return t('whatsappCampaigns.editor.readOnly');
                  const reason = templateUnavailableReason(template, kind);
                  return reason ? t(`whatsappCampaigns.editor.templateUnavailable.${reason}`) : null;
                }}
                onSelect=${(/** @type {WhatsappTemplate} */ template) => change({ templateId: template.id, variableMappings: { body: {}, header: {} } })}
                createHref=${readOnly ? null : templateCreateHref} onRefresh=${readOnly ? null : refreshTemplates} isRefreshing=${isSyncing} />`}
          ${selectedTemplate
            ? html`<div class="wa-editor-message-grid">
                <div>
                  <h3 class="wa-editor-subtitle">${t('whatsappCampaigns.variables.title')}</h3>
                  <${VariableMappingEditor} template=${selectedTemplate} mappings=${variableMappings} missing=${missingVariables} disabled=${readOnly}
                    onChange=${(mappings) => change({ variableMappings: mappings })} />
                </div>
                <${WhatsappMessagePreview} template=${selectedTemplate} values=${sample.values}
                  samples=${precheck?.sample ? [{ label: precheck.sample.to?.displayName ?? '', values: sample.values, fallbackVariables: sample.fallbackVariables }] : null} />
              </div>`
            : null}
        <//>

        <${EditorCard} number=${4} title=${t('whatsappCampaigns.editor.steps.sending')} testId="wa-editor-sending">
          <div class="wa-editor-option">
            <${Toggle} value=${view.options?.sendWindow?.enabled !== false} disabled=${readOnly} ariaLabel=${t('whatsappCampaigns.editor.businessHoursLabel')}
              onChange=${(enabled) => change({ options: { ...(view.options ?? {}), sendWindow: { enabled } } })} />
            <span>${t('whatsappCampaigns.editor.businessHours', businessHours)}</span>
          </div>
          ${kind === 'MARKETING'
            ? html`<label class="wa-editor-option">
                <span>${t('whatsappCampaigns.editor.frequencyCapBefore')}</span>
                <input class="text-input wa-editor-hours" type="number" min="0" max=${WHATSAPP_CAMPAIGN_LIMITS.maximumFrequencyCapHours} disabled=${readOnly}
                  value=${view.options?.frequencyCapHours ?? WHATSAPP_CAMPAIGN_LIMITS.defaultFrequencyCapHours}
                  aria-label=${t('whatsappCampaigns.editor.frequencyCapLabel')}
                  onChange=${(/** @type {Event} */ event) => {
                    const hours = Math.max(0, Math.min(WHATSAPP_CAMPAIGN_LIMITS.maximumFrequencyCapHours, Math.round(Number(/** @type {HTMLInputElement} */ (event.currentTarget).value) || 0)));
                    change({ options: { ...(view.options ?? {}), frequencyCapHours: hours } });
                  }} />
                <span>${t('whatsappCampaigns.editor.frequencyCapAfter')}</span>
              </label>`
            : null}
          ${mediaCampaigns
            ? html`<label class="form-field">
                <span class="form-field-label">${t('whatsappCampaigns.editor.mediaCampaign')}</span>
                <select class="text-input integration-select" disabled=${readOnly} value=${view.mediaCampaignId ?? campaign.mediaCampaign?.id ?? ''}
                  onChange=${(/** @type {Event} */ event) => change({ mediaCampaignId: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
                  <option value="">${t('whatsappCampaigns.editor.noMediaCampaign')}</option>
                  ${mediaCampaigns.map((item) => html`<option key=${item.id} value=${item.id} selected=${item.id === (view.mediaCampaignId ?? campaign.mediaCampaign?.id)}>${item.name}</option>`)}
                </select>
              </label>`
            : null}
          <p class="wa-editor-help">${t('whatsappCampaigns.editor.sendingHelp')}</p>
        <//>
      </div>
      <${EditorSummary} precheck=${precheck} state=${precheckState} error=${precheckError} missing=${missing} />
    </div>
    ${isReviewOpen
      ? html`<${WhatsappCampaignReviewModal} campaign=${campaign} template=${selectedTemplate} onClose=${() => setIsReviewOpen(false)}
          onDone=${(/** @type {WhatsappCampaign} */ updated) => {
            setIsReviewOpen(false);
            setCampaign(updated);
            onChanged(updated);
          }} />`
      : null}
    ${isTestOpen ? html`<${WhatsappCampaignTestDialog} campaign=${campaign} template=${selectedTemplate} values=${sample.values} onClose=${() => setIsTestOpen(false)} beforeSend=${saveNow} />` : null}
    ${isDeleteOpen
      ? html`<${Modal} title=${t('whatsappCampaigns.confirm.deleteTitle', { name: campaign.name })} subtitle=${t('whatsappCampaigns.confirm.delete')}
          confirmLabel=${t('whatsappCampaigns.actions.delete')} confirmAccent="danger" onClose=${() => setIsDeleteOpen(false)} onConfirm=${remove} />`
      : null}
  <//>`;
}

/** Esqueleto dos quatro cartões e do resumo, enquanto a campanha carrega. */
export function WhatsappCampaignEditorSkeleton() {
  return html`<div class="wa-editor-layout" aria-busy="true">
    <div class="wa-editor-main">${[0, 1, 2, 3].map((index) => html`<${Skeleton} key=${index} height=${140} radius=${8} />`)}</div>
    <div class="wa-editor-summary"><${Skeleton} height=${220} radius=${8} /></div>
  </div>`;
}
